import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { TIMELINE_STEP_MINUTES, type TimelineRange } from "../logic/dayTime";
import type { MarkerTone, TimelineMarker } from "../logic/momentFocus";
import { snapTimelineMinute } from "../logic/timelineSnap";

// Докосване на палеца: колко пиксела от центъра му се броят за „на палеца"
// и колко трябва да се мръдне, преди да започне да мести.
const THUMB_HIT_PX = 14;
const DRAG_SLOP_PX = 6;
// Shift + стрелка / PageUp / PageDown.
const BIG_STEP_MINUTES = 60;
// Над колко гости на слот графиката е с пълна височина (по-тихо ден не
// изглежда претъпкан с един 2-местен стълб).
const LOAD_FULL_SCALE_GUESTS = 12;

const MARKER_CLASS: Record<MarkerTone, string> = {
  upcoming: "bg-schema-reserved-edge",
  seated: "bg-schema-occupied-edge",
  done: "bg-faint",
  missed: "border border-faint bg-surface",
};

/**
 * Пистата на времевата линия: графиката „натовареност" (гостите на масите
 * през 15 мин) над нея, маркерите на резервациите под нея, чертичката
 * „сега" в прегледа и палецът с балон по време на плъзгане. Своя обработка
 * на pointer събитията (точно съответствие пиксел → минута, тъч през
 * `touch-pan-y` — вертикалният скрол на страницата остава) и role="slider"
 * с клавиатура: ←/→ по 5 мин, Shift+←/→ и PageUp/PageDown по час, Home/End.
 */
const TimelineTrack = ({
  sliderMinute,
  range,
  isOutside,
  nowMinute,
  liveMinute,
  markers,
  load,
  valueText,
  onChange,
}: {
  sliderMinute: number;
  range: TimelineRange;
  isOutside: boolean;
  // Минутата на „сега", когато е в показания ден и не сме на нея (иначе null).
  nowMinute: number | null;
  // Минутата на „сега" в днешния ден (и на живо) — за магнита; иначе null.
  liveMinute: number | null;
  markers: TimelineMarker[];
  load: number[];
  valueText: string;
  onChange: (minute: number) => void;
}) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Моментното състояние за pointermove — събитията идват по-бързо от рендера.
  const draggingRef = useRef(false);
  // Натиснато върху палеца: докато не се мръдне с DRAG_SLOP_PX, стойността
  // не се пипа — случайно докосване не вади от „сега" и не мести с 5 мин.
  const grabRef = useRef<{ startX: number } | null>(null);
  const span = range.endMinute - range.startMinute;
  const percentOf = (minute: number) => ((minute - range.startMinute) / span) * 100;
  const clamp = (minute: number) => Math.min(range.endMinute, Math.max(range.startMinute, minute));

  const minuteAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return sliderMinute;
    const raw = range.startMinute + ((clientX - rect.left) / rect.width) * span;
    return snapTimelineMinute(raw, { range, liveMinute, markerMinutes: markers.map((marker) => marker.minute) });
  };

  const isOnThumb = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return false;
    const thumbX = rect.left + (percentOf(sliderMinute) / 100) * rect.width;
    return Math.abs(clientX - thumbX) <= THUMB_HIT_PX;
  };

  const emit = (minute: number) => {
    if (minute !== sliderMinute) onChange(minute);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    draggingRef.current = true;
    setIsDragging(true);
    if (isOnThumb(event.clientX)) {
      grabRef.current = { startX: event.clientX };
      return;
    }
    grabRef.current = null;
    emit(minuteAt(event.clientX));
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    if (grabRef.current) {
      if (Math.abs(event.clientX - grabRef.current.startX) < DRAG_SLOP_PX) return;
      grabRef.current = null;
    }
    emit(minuteAt(event.clientX));
  };

  const stopDragging = () => {
    draggingRef.current = false;
    grabRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? BIG_STEP_MINUTES : TIMELINE_STEP_MINUTES;
    // Към стъпката: 19:13 → ← 19:10, → 19:15.
    const down = Math.ceil(sliderMinute / step) * step - step;
    const up = Math.floor(sliderMinute / step) * step + step;
    const next: Record<string, number> = {
      ArrowLeft: down,
      ArrowDown: down,
      ArrowRight: up,
      ArrowUp: up,
      PageDown: sliderMinute - BIG_STEP_MINUTES,
      PageUp: sliderMinute + BIG_STEP_MINUTES,
      Home: range.startMinute,
      End: range.endMinute,
    };
    if (!(event.key in next)) return;
    event.preventDefault();
    emit(clamp(next[event.key]));
  };

  const thumbPercent = percentOf(sliderMinute);
  const slotSpan = span / Math.max(1, load.length);
  const loadScale = Math.max(LOAD_FULL_SCALE_GUESTS, ...load);
  const hasLoad = load.some((guests) => guests > 0);

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={t("reservations.timeline.slider")}
      aria-valuemin={range.startMinute}
      aria-valuemax={range.endMinute}
      aria-valuenow={sliderMinute}
      aria-valuetext={valueText}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
      onKeyDown={handleKeyDown}
      className="group relative min-w-0 flex-1 cursor-pointer touch-pan-y select-none rounded-[10px] pb-6 pt-[22px] outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
    >
      {/* Натовареността: гостите на масите във всеки 15-минутен слот. */}
      {hasLoad && (
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-1 flex h-4 items-end gap-px">
          {load.map((guests, index) => {
            const slotStart = range.startMinute + index * slotSpan;
            const isCurrent = !isOutside && sliderMinute >= slotStart && sliderMinute < slotStart + slotSpan;
            const isPast = slotStart + slotSpan <= sliderMinute;
            return (
              <span
                key={index}
                className={`min-w-0 flex-1 rounded-t-[2px] transition-[height,background-color] duration-200 ${
                  guests === 0
                    ? "bg-transparent"
                    : isCurrent
                      ? "bg-brand"
                      : isPast
                        ? "bg-brand/35"
                        : "bg-ink-2/15"
                }`}
                // Височина от данните → inline style (правило 3, изключение 1).
                style={{ height: guests === 0 ? 0 : `${Math.max(18, (guests / loadScale) * 100)}%` }}
              />
            );
          })}
        </div>
      )}

      <div ref={trackRef} className="relative h-[6px] rounded-full bg-schema-wall">
        {!isOutside && (
          <div
            className={`absolute inset-y-0 left-0 rounded-full bg-brand ${
              isDragging ? "" : "transition-[width] duration-200 ease-out"
            }`}
            // Позиция, известна чак по време на изпълнение → inline style (правило 3, изключение 1).
            style={{ width: `${thumbPercent}%` }}
          />
        )}

        {markers.map((marker) => (
          <span
            key={marker.minute}
            aria-hidden
            className={`pointer-events-none absolute top-[calc(100%+4px)] -translate-x-1/2 rounded-full ${
              marker.count > 1 ? "size-[7px]" : "size-[5px]"
            } ${MARKER_CLASS[marker.tone]}`}
            style={{ left: `${percentOf(marker.minute)}%` }}
          />
        ))}

        {nowMinute !== null && (
          <span
            aria-hidden
            className="pointer-events-none absolute top-1/2 h-4 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-surface bg-ink"
            style={{ left: `${percentOf(nowMinute)}%` }}
          />
        )}

        <div
          className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 ${
            isDragging ? "" : "transition-[left] duration-200 ease-out"
          }`}
          style={{ left: `${thumbPercent}%` }}
        >
          <div
            className={`rounded-full border-2 shadow-[0_1px_3px_rgba(28,25,23,0.25)] transition-transform duration-150 ${
              isOutside ? "border-brand bg-surface" : "border-surface bg-brand"
            } ${isDragging ? "size-5" : "size-4 group-hover:scale-110"}`}
          />
          {isDragging && (
            <div className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-0.5 text-[12px] font-bold tabular-nums text-surface shadow-[0_4px_12px_rgba(28,25,23,0.2)]">
              {valueText}
            </div>
          )}
        </div>
      </div>

      {range.tickHours.map((hour) => (
        <span
          key={hour}
          // Първият надпис на самия ръб не се реже наполовина.
          className={`pointer-events-none absolute bottom-0 text-[11px] font-medium tabular-nums text-muted ${
            hour * 60 === range.startMinute ? "" : "-translate-x-1/2"
          }`}
          style={{ left: `${percentOf(hour * 60)}%` }}
        >
          {hour}
        </span>
      ))}
    </div>
  );
};

export default TimelineTrack;
